import { dateFormatter, DateFormatTypes, isEmpty, isEmptyObject, roundOfToTwoDigits } from '@jfs/utils'
import { flow, types } from 'mobx-state-tree'
import { exportCampaign } from '../../network/exportCampaign'
import { extendAppSessionAPI } from '../../network/extendAppSession'
import { extendUserSessionAPI } from '../../network/extendUserSession'
import { getCampaignDetails } from '../../network/getCampaignDetails'
import { getCampaignGraphData } from '../../network/getCampaignGraphData'
import { getCampaigns } from '../../network/getCampaigns'
import { getCampaignsSummary } from '../../network/getCampaignsSummary'
import { getMasterList } from '../../network/getMasterList'
import { updateCampaign } from '../../network/updateCampaign'
import { getSelectedDate, removeDuplicateElement } from '../../util/util'
import { mimeTypes } from '../static/data'
import { customError } from '../static/strings'
import { RootStore } from '../store'
import { Campaign } from './Campaign'

const PaymentMode = types.model('PaymentMode', {
  id: types.optional(types.string, ''),
  value: types.optional(types.string, ''),
  selected: types.optional(types.boolean, false),
})

const InstrumentValues = types.model('InstrumentValues', {
  id: types.optional(types.number, 0),
  value: types.optional(types.string, ''),
  tenures: types.optional(types.array(types.string), []),
})
const Instrument = types.model('Instrument', {
  id: types.optional(types.string, ''),
  type: types.optional(types.string, ''),
  subType: types.optional(types.string, ''),
  values: types.optional(types.array(InstrumentValues), []),
})

const DistributionCount = types.model('DistributionCount', {
  id: types.optional(types.string, ''),
  date: types.optional(types.string, ''),
  value: types.optional(types.number, 0),
})

const Highlights = types.model('Highlights', {
  id: types.optional(types.string, ''),
  label: types.optional(types.string, ''),
  value: types.optional(types.string, ''),
  tooltip: types.optional(types.string, ''),
})

const Position = types.model('position', {
  left: types.number,
  top: types.number,
  visible: types.boolean,
})

const DateRange = types.model('date', {
  fromDate: types.Date,
  toDate: types.Date,
  position: Position,
  dateLabel: types.string,
})

const productSelection = types.model('product', {
  id: types.optional(types.number, 0),
  value: types.optional(types.string, ''),
  selected: types.optional(types.boolean, false),
  data: types.optional(types.array(types.number), []),
})
const RuleConfigError = types.model('RuleConfigError', {
  txnAmountKey: types.maybeNull(types.string),
  txnAmount: types.maybeNull(types.string),
  maxAmount: types.maybeNull(types.string),
  minAmount: types.maybeNull(types.string),
  paymentMode: types.maybeNull(types.string),
  paymentInstrument: types.maybeNull(types.string),
  purchseType: types.maybeNull(types.string),
  roi: types.maybeNull(types.string),
  tenure: types.maybeNull(types.string),
  emiTenure: types.maybeNull(types.string),
  emiBankName: types.maybeNull(types.string),
  bin: types.maybeNull(types.string),
  purchaseType: types.maybeNull(types.string),
  txnAttributeExcluded1: types.maybeNull(types.string),
  txnAttributeExcluded2: types.maybeNull(types.string),
  txnAttributeExcluded3: types.maybeNull(types.string),
  txnAttributeExcluded4: types.maybeNull(types.string),
  txnAttributeExcluded5: types.maybeNull(types.string),
  txnAttributeExcluded6: types.maybeNull(types.string),
  txnAttributeIncluded1: types.maybeNull(types.string),
  txnAttributeIncluded2: types.maybeNull(types.string),
  txnAttributeIncluded3: types.maybeNull(types.string),
  txnAttributeIncluded4: types.maybeNull(types.string),
  txnAttributeIncluded5: types.maybeNull(types.string),
  txnAttributeIncluded6: types.maybeNull(types.string),
  txnAttributeKey1: types.maybeNull(types.string),
  txnAttributeKey2: types.maybeNull(types.string),
  txnAttributeKey3: types.maybeNull(types.string),
  txnAttributeKey4: types.maybeNull(types.string),
  txnAttributeKey5: types.maybeNull(types.string),
  txnAttributeKey6: types.maybeNull(types.string),
})

const Config = types.model('config', {
  productSelection: types.optional(types.array(productSelection), []),
  ruleConfigErrors: types.optional(RuleConfigError, {}),
  txnAttributesCount: types.optional(types.number, 0),
})
const Campaigns = types
  .model('Campaigns', {
    campaigns: types.optional(types.array(Campaign), []),
    searchedCampaign: types.optional(Campaign, {}),
    paymentModes: types.optional(types.array(PaymentMode), []),
    paymentInstruments: types.optional(types.array(Instrument), []),
    graphData: types.optional(types.array(DistributionCount), []),
    duration: types.optional(types.string, 'Weekly'),
    highlights: types.optional(types.array(Highlights), []),
    selectedDateInfo: DateRange,
    config: Config,
  })
  .actions((self) => ({
    fetchCampaigns: flow(function* fetchCampaigns() {
      let response
      const selectedDates = self.selectedDateInfo
      const offerFilters = RootStore.transactions.filter.campaignType
      const statusFilters = RootStore.transactions.filter.campaignStatus

      const getOfferkey: any = {
        'Instant Discount': 1,
        Cashback: 2,
      }

      const getStatuskey: any = {
        Active: 1,
        InActive: 2,
        Paused: 3,
        Scheduled: 4,
        Completed: 5,
      }

      const offerFilterKeys = () => {
        const retData: any = []
        offerFilters.map((item: string) => {
          if (item.toUpperCase() !== 'ALL') {
            getOfferkey[item] && retData.push(getOfferkey[item])
          }
        })
        return retData
      }

      const statusFilterKeys = () => {
        const retData: any = []
        statusFilters.map((item: any) => {
          if (item !== 'All') {
            getStatuskey[item] && retData.push(getStatuskey[item])
          }
        })
        return retData
      }

      const reqBody = {
        merchantId: RootStore.user.selectedMid,
        filter: {
          startDate: dateFormatter(selectedDates.fromDate, DateFormatTypes.DATE_HYPHEN),
          endDate: dateFormatter(selectedDates.toDate ?? new Date(Date.now()), DateFormatTypes.DATE_HYPHEN),
          offerType: offerFilterKeys(),
          status: statusFilterKeys(),
        },
      }
      try {
        response = yield getCampaigns(reqBody, RootStore.user.session.getTokens, self.paymentInstruments)
        self.campaigns = response
      } catch (e) {
        RootStore?.response?.setMessage(e?.data?.error?.message ?? customError.errorOccured)
        throw new Error(e?.data?.error?.message ?? 'Error while processing request')
      }
      return response
    }),
    fetchCampaignsSummary: flow(function* fetchCampaignsSummary() {
      let response
      const selectedDates = RootStore.settings.metaData.selectedDate
      const offerFilters = RootStore.transactions.filter.campaignType
      const statusFilters = RootStore.transactions.filter.campaignStatus
      const getOfferkey: any = {
        'Instant Discount': 1,
        Cashback: 2,
      }

      const getStatuskey: any = {
        Active: 1,
        InActive: 2,
        Paused: 3,
        Scheduled: 4,
        Completed: 5,
      }

      const offerFilterKeys = () => {
        const retData: any = []
        offerFilters.map((item: any) => {
          if (item !== 'All') {
            retData.push(getOfferkey[item])
          }
        })
        return retData
      }

      const statusFilterKeys = () => {
        const retData: any = []
        statusFilters.map((item: any) => {
          if (item !== 'All') {
            retData.push(getStatuskey[item])
          }
        })
        return retData
      }
      // const toDate =
      //   selectedDates.dateLabel === 'Custom Range' || selectedDates.dateLabel === actionMenuStrings.yesterday
      //     ? selectedDates.toDate
      //     : new Date(Date.now())
      const reqBody = {
        merchantId: RootStore.user.selectedMid,
        filter: {
          startDate: dateFormatter(selectedDates.fromDate, DateFormatTypes.DATE_HYPHEN),
          endDate: dateFormatter(selectedDates.toDate, DateFormatTypes.DATE_HYPHEN),
          offerType: offerFilterKeys(),
          status: statusFilterKeys(),
        },
      }
      try {
        response = yield getCampaignsSummary(reqBody, RootStore.user.session.getTokens)
        self.highlights = JSON.parse(JSON.stringify(response))
      } catch (e) {
        RootStore?.response?.setMessage(e?.data?.error?.message ?? customError.errorOccured)
        throw new Error(e?.data?.error?.message ?? 'Error while processing request')
      }
      return response
    }),
    fetchCampaignDetails: flow(function* fetchCampaignDetails(id: string) {
      let response: any
      let own: any = self
      const reqBody = {
        merchantId: RootStore.user.selectedMid,
        offer: {
          offerCode: id,
        },
      }
      try {
        response = yield getCampaignDetails(reqBody, RootStore.user.session.getTokens, self.paymentInstruments)
        own.resetProducts()
        const selectedCategories: any = []
        if (!isEmptyObject(response?.products)) {
          Object.keys(response?.products)?.map((_, index) => {
            const selectedCategory = response?.products?.[`txnAttributeKey${index + 1}`]
            !!selectedCategory && selectedCategories.push(selectedCategory)
          })
        }
        const parsedProducts: any = []
        selectedCategories?.length > 0
          ? (self as any).updateTxnAttributesCount(selectedCategories?.length)
          : (self as any).updateTxnAttributesCount(1)
        self?.config?.productSelection?.map((item) => {
          let retObj = item
          selectedCategories?.map((selectedCategory: any) => {
            if (selectedCategory === item?.value) {
              retObj = {
                ...item,
                selected: true,
              }
              parsedProducts?.indexOf(retObj) === -1 && parsedProducts.push(retObj)
            }
          })
          parsedProducts?.indexOf(retObj) === -1 && parsedProducts.push(retObj)
        })
        self.config.productSelection = JSON.parse(JSON.stringify(parsedProducts))
        if (response?.cId === id) {
          //const oldData = self.campaigns
          self.searchedCampaign = response
          // const retData: any = []
          // oldData.map((item: any) => {
          //   let retItem
          //   if (item?.cId === id) {
          //     retItem = response
          //   } else {
          //     retItem = item
          //   }
          //   retData.push(JSON.parse(JSON.stringify(retItem)))
          // })
          // self.campaigns = JSON.parse(JSON.stringify(retData))
        } else {
          RootStore?.response?.setMessage(customError.errorOccured)
          throw 'Offer ID does not exist'
        }
      } catch (e) {
        RootStore?.response?.setMessage(typeof e === 'string' ? e : e?.data?.error?.message ?? customError.errorOccured)
        throw new Error(e?.data?.error?.message ?? 'Error while processing request')
      }
      return response
    }),
    updateSelectedDateInfo(dateObj: any): void {
      self.selectedDateInfo.fromDate = dateObj?.fromDate ?? new Date(Date.now())
      self.selectedDateInfo.toDate = dateObj?.toDate ?? new Date(Date.now())
      self.selectedDateInfo.position.top = dateObj?.position?.top ?? 0
      self.selectedDateInfo.position.left = dateObj?.position?.left ?? 0
      self.selectedDateInfo.position.visible = dateObj?.position?.visible ?? false
      self.selectedDateInfo.dateLabel = dateObj?.dateLabel ?? ''
    },
    fetchGraphData: flow(function* fetchGraphData(id: any) {
      let response
      const getStartDate = (duration: string) => {
        const getDate = (): Date => {
          const value: any = {
            Weekly: 7,
            Monthly: 30,
          }
          const today = new Date(Date.now())
          return new Date(today.setDate(today.getDate() - value[duration]))
        }
        return getDate()
      }
      const startDate = getStartDate(self.duration)
      const reqBody = {
        merchantId: RootStore.user.selectedMid,
        offerCode: id,
        startDate: dateFormatter(startDate, DateFormatTypes.YYYYMMDD),
        endDate: dateFormatter(new Date(Date.now()), DateFormatTypes.YYYYMMDD),
      }
      try {
        response = yield getCampaignGraphData(reqBody, RootStore.user.session.getTokens)
        self.graphData = JSON.parse(JSON.stringify(response))
      } catch (e) {
        RootStore?.response?.setMessage(e?.data?.error?.message ?? customError.errorOccured)
        throw new Error(e?.data?.error?.message ?? 'Error while processing request')
      }
      return response
    }),
    createCampaign: flow(function* createCampaign(data: any, id: any, isDuplicate: boolean, type?: number) {
      let response: any
      const metaData: any = []
      self.paymentInstruments.map((item) => {
        metaData.push(...item.values)
      })
      const getIdForPaymentInstrument= (paymentInstrumentType,key: any) => {
        const metaData= self.paymentInstruments?.find(item=>item?.type==paymentInstrumentType)?.values??[];
       return metaData?.find((item: any) => item?.value?.toString() === key?.toString())?.id ?? ''
      }

      const getInstrumentValue = (key: any) => {
        const retData: any = []
        let keys: any = key
        if (typeof keys === 'string') {
          keys = key?.split(',') ?? []
        }
        metaData?.map((item: any) => {
          keys?.map((element: any) => {
            if (item?.value?.toString() === element?.toString()) {
              retData.push(`${item?.id}`)
            }
          })
        })
        return retData ?? []
      }

      const getModeValue = (key: any) => {
        const allModeValues: any = {
          wallet: 579,
          netbanking: 581,
          card: 582,
          'no cost emi': 582,
        }
        return Number(allModeValues[key?.toLowerCase()] ?? 0)
      }

      try {
        const instrumentsData: any = []
        self.paymentInstruments?.map((item) => {
          item?.values?.map((element) => {
            instrumentsData.push(element)
          })
        })

        const getEmiObj = () => {
          if (data?.ruleConfig.paymentMode === 'No Cost EMI') {
            const tenure = data?.ruleConfig?.tenure
            const tenureData = typeof tenure === 'string' ? tenure?.split(',') : tenure
            const roi =
              typeof data?.ruleConfig?.roi === 'string' ? data?.ruleConfig?.roi?.split(',') : data?.ruleConfig?.roi
            const retData = []
            tenureData?.map((item, index) => {
              retData.push({
                tenure: item?.endsWith('M') ? item : `${item}M`,
                bankEmiId: data?.ruleConfig?.emiBankName
                  ? `${getIdForPaymentInstrument("bankEmiId",data?.ruleConfig?.emiBankName[index])}`
                  : undefined,
                rate: roi?.length > 0 ? Number(roi?.[index]) : undefined,
              })
            })
            const filteredData = removeDuplicateElement(retData)
            return filteredData
          } else if (
            data?.ruleConfig?.paymentMode === 'Card' &&
            data?.ruleConfig?.purchaseType?.includes('EMI Purchase')
          ) {
            const tenure = data?.ruleConfig?.emiTenure
            const tenureData = removeDuplicateElement(typeof tenure === 'string' ? tenure?.split(',') : tenure)
            const bankNames = removeDuplicateElement(
              typeof data?.ruleConfig?.emiBankName === 'string'
                ? data?.ruleConfig?.emiBankName?.split(',')
                : data?.ruleConfig?.emiBankName,
            )
            const retData = []
            tenureData?.map((tenureItem, index) => {
              bankNames?.map((bankName, j) => {
                retData.push({
                  tenure: tenureItem?.endsWith('M') ? tenureItem : `${tenureItem}M`,
                  bankEmiId: data?.ruleConfig?.emiBankName ? `${getIdForPaymentInstrument("bankEmiId",bankName)}` : undefined,
                })
              })
            })
            return retData
          }
        }
        const getValues = () => {
          const value = {
            bankId:
              data?.ruleConfig?.paymentMode === 'NetBanking' || data?.ruleConfig?.paymentMode === 'Wallet'
                ? getInstrumentValue(data?.ruleConfig?.paymentInstrument)
                : undefined,
            emi: getEmiObj(),
            bins:
              data?.ruleConfig?.paymentMode === 'Card' || data?.ruleConfig?.paymentMode === 'No Cost EMI'
                ? data?.ruleConfig?.bin
                : undefined,
            maxCount: data?.ruleConfig?.maxLimit ? Number(data?.ruleConfig?.maxLimit) : undefined,
          }
          return value
        }

        const getTime = (time: any) => {
          const dateObj = new Date(time)
          const hr = dateObj.getHours()
          const mins = dateObj.getMinutes()
          return `${roundOfToTwoDigits(hr)}:${roundOfToTwoDigits(mins)}:00`
        }
        const getOfferObj = () => {
          if (!!type && type > 0) {
            return {
              offerCode: data?.campaignId,
            }
          }

          const startDate = new Date(data?.startDate)
          const startTIme = data?.startDate

          return {
            offerCode: data?.campaignId,
            offerDescription: data?.description,
            offerName: data?.title,
            startDate: dateFormatter(startDate, DateFormatTypes.YYYYMMDD),
            endDate: dateFormatter(new Date(data?.endDate), DateFormatTypes.YYYYMMDD),
            startTime: getTime(startTIme),
            endTime: getTime(data?.endDate),
            termsConditions: data?.tnc,
            offerType: data?.ruleConfig?.paymentMode === 'No Cost EMI' ? 1 : getIdForPaymentInstrument('rewardType',data?.offerType),
            unitType: data?.ruleConfig?.paymentMode === 'No Cost EMI' ? 3 : getIdForPaymentInstrument('unitType',data?.discountType),
            unitValue:
              data?.ruleConfig?.paymentMode === 'No Cost EMI'
                ? 1
                : data?.discountType === 'Fixed'
                ? Number(data?.rewardAmount)
                : Number(data?.rewardPercentage),
            maxOfferDiscount: Number(data?.totalBudget ?? 0)?.toFixed(2),
            maxTransactionDiscount:
              data?.ruleConfig?.paymentMode === 'No Cost EMI' ? '0' : Number(data?.maxCashback ?? 0)?.toFixed(2),
            status: type ? undefined : 1,
            maxCount: Number(data?.totalCount ?? 0),
            combinedWith: data?.linkedCampaignId?.length > 0 ? data?.linkedCampaignId?.join(',') : undefined,
            minOrderAmount:
              data?.ruleConfig?.txnAmountKey === 'Greater than'
                ? Number(data?.ruleConfig?.txnAmount ?? 0)?.toFixed(2)
                : data?.ruleConfig?.txnAmountKey === 'Range'
                ? Number(data?.ruleConfig?.minAmount ?? 0)?.toFixed(2)
                : undefined,
            maxOrderAmount:
              data?.ruleConfig?.txnAmountKey === 'Less than'
                ? Number(data?.ruleConfig?.txnAmount ?? 0)?.toFixed(2)
                : data?.ruleConfig?.txnAmountKey === 'Range'
                ? Number(data?.ruleConfig?.maxAmount ?? 0)?.toFixed(2)
                : undefined,
          }
        }
        const getPurchaseTypes = (purchaseTypes: any) => {
          const purchaseTypeString: any = {
            'Regular Purchase': 110,
            'EMI Purchase': 114,
          }
          const data = typeof purchaseTypes === 'string' ? purchaseTypes?.split(',') : purchaseTypes
          return data?.map((item: any) => purchaseTypeString[item])
        }
        const getType = (paymode: string) => {
          const types: any = {
            Card: getPurchaseTypes(data?.ruleConfig?.purchaseType),
            'No Cost EMI': [114],
          }
          return types[paymode] ?? [110]
        }
        const getProductObj = (data: any) => {
          const values = Object.values(data)
          const keys = Object.keys(data)
          const getValue = (val: string, key: any) => {
            const index = values.indexOf(val)
            // const index1= values.findIndex(val)
            const i = keys[index]?.slice(-1)
            return i ? data[`${key}${i}`] : undefined
          }
          // const prodKeys = keys.filter((item: any) => item.startsWith('txnAttributeKey') && !!data[item] && data[item].length > 0)
          return {
            productid_included: getValue('Product Id', 'txnAttributeIncluded'),
            productid_excluded: getValue('Product Id', 'txnAttributeExcluded'),
            category1_included: getValue('Category Id - 1', 'txnAttributeIncluded'),
            category2_included: getValue('Category Id - 2', 'txnAttributeIncluded'),
            category3_included: getValue('Category Id - 3', 'txnAttributeIncluded'),
            category4_included: getValue('Category Id - 4', 'txnAttributeIncluded'),
            category5_included: getValue('Category Id - 5', 'txnAttributeIncluded'),
            category1_excluded: getValue('Category Id - 1', 'txnAttributeExcluded'),
            category2_excluded: getValue('Category Id - 2', 'txnAttributeExcluded'),
            category3_excluded: getValue('Category Id - 3', 'txnAttributeExcluded'),
            category4_excluded: getValue('Category Id - 4', 'txnAttributeExcluded'),
            category5_excluded: getValue('Category Id - 5', 'txnAttributeExcluded'),
          }
        }
        const prodObj = data?.ruleConfig ? getProductObj(data?.ruleConfig) : {}
        const reqBody = type
          ? {
              merchantId: RootStore.user.selectedMid,
              action: type,
              offer: getOfferObj(),
            }
          : {
              merchantId: RootStore.user.selectedMid,
              action: type,
              offer: getOfferObj(),
              product: !isEmptyObject(JSON.parse(JSON.stringify(prodObj))) ? prodObj : undefined,
              method: {
                type: getType(data?.ruleConfig?.paymentMode),
                subType: getModeValue(data?.ruleConfig?.paymentMode),
                values: getValues(),
              },
            }
        response = yield updateCampaign(reqBody, RootStore.user.session.getTokens, id, isDuplicate)
        if (response.status === 'SUCCESS') {
          return (self as any).fetchCampaigns()
        } else {
          if (response?.error?.code !== '33607' && response?.error?.code !== '33306') {
            throw { data: { error: { message: response?.data?.failureMessage ?? customError.errorOccured } } }
          }
        }
      } catch (e) {
        RootStore?.response?.setMessage(e?.data?.error?.message ?? customError.errorOccured)
        throw new Error(e?.data?.error?.message ?? 'Error while processing request')
      }
    }),
    getPaymentModesMasterList: flow(function* getPaymentModesMasterList() {
      if (self.paymentInstruments?.length > 0) {
        return
      }
      let response
      const reqBody = {
        merchantId: RootStore.user.selectedMid,
      }
      try {
        response = yield getMasterList(reqBody, RootStore.user.session.getTokens)
        self.paymentInstruments = JSON.parse(JSON.stringify(response?.instruments))
        self.paymentModes = JSON.parse(JSON.stringify(response?.modes))
      } catch (e) {
        RootStore?.response?.setMessage(e?.data?.error?.message ?? customError.errorOccured)
        throw new Error(e?.data?.error?.message ?? 'Error while processing request')
      }
      return response
    }),
    exportCampaignReport: flow(function* exportCampaignReport(type, id) {
      const fileTypes: any = {
        Text: 'TXT',
        Excel: 'XLSX',
      }
      const reqBody = {
        merchantId: RootStore.user.selectedMid,
        mediaType: fileTypes[type],
        offer: {
          offerCode: id,
        },
      }
      let resp
      try {
        resp = yield exportCampaign(RootStore.user.session.getTokens, reqBody)
        // Below is to check the errorcode in the response as its arraybuffer-
        try {
          const errorFileResponse =
            resp?.data?.status !== 'SUCCESS' && JSON.parse(Buffer.from(resp?.data).toString('utf8'))
          const errorCode = errorFileResponse?.error?.code ?? ''
          if (errorCode === '33607' || errorCode === '33306') {
            if (errorCode === '33607') {
              extendUserSessionAPI().then((response) => {
                console.log('extendUserSessionAPI response', response)
                return (self as any).exportCampaignReport(type, id)
              })
            } else {
              extendAppSessionAPI().then((response) => {
                console.log('extendAppSessionAPI response', response)
                return (self as any).exportCampaignReport(type, id)
              })
            }
          }
        } catch (e) {
          console.log({ e })
        }
        if (window) {
          let mimeType = resp.headers['content-type']
          //check for undefined file download on session expire
          if (mimeTypes.get(mimeType) === undefined) {
            return
          }
          let fileInfo = resp.headers['content-disposition']
          const fileName = fileInfo?.slice(fileInfo.indexOf('=') + 1, fileInfo.lastIndexOf('.')) ?? 'file'
          const blob = new Blob([resp?.data], {
            mimeType,
          })
          const url = window.URL.createObjectURL(blob)
          const a = document.createElement('a')
          document.body.appendChild(a)
          a.href = url
          a.download = fileName + `${mimeTypes.get(mimeType)}`
          a.click()
          window.URL.revokeObjectURL(url)
          document.body.removeChild(a)
        }
      } catch (e) {
        RootStore?.response?.setMessage(e?.data?.error?.message ?? 'Error while processing request')
      }
      return resp
    }),

    updatePaymentModeSelection(mode: any): any {
      const newData: any = []
      self.paymentModes?.map((item) => {
        if (item.value === mode) {
          const updatedItem = {
            ...item,
            selected: true,
          }
          newData.push(updatedItem)
        } else {
          newData.push(item)
        }
      })
      self.paymentModes = JSON.parse(JSON.stringify(newData))
    },
    resetPaymentModes(): any {
      self.paymentModes = JSON.parse(JSON.stringify([]))
    },
    updateDuration(duration: string): any {
      self.duration = duration
    },
    updateProductSelection(product: any): any {
      const newData: any = []
      self.config.productSelection?.map((item) => {
        if (item.value === product) {
          const updatedItem = {
            ...item,
            selected: item.selected === true ? false : true,
          }
          newData.push(updatedItem)
        } else {
          newData.push(item)
        }
      })
      self.config.productSelection = JSON.parse(JSON.stringify(newData))
    },
    resetProducts(): any {
      self.config.productSelection = JSON.parse(
        JSON.stringify([
          { id: 1, value: 'Category Id - 1', selected: false },
          { id: 2, value: 'Category Id - 2', selected: false },
          { id: 3, value: 'Category Id - 3', selected: false },
          { id: 4, value: 'Category Id - 4', selected: false },
          { id: 5, value: 'Category Id - 5', selected: false },
          { id: 6, value: 'Product Id', selected: false },
        ]),
      )
    },
    updateTxnAttributesCount(count: number) {
      self.config.txnAttributesCount = count
    },
    updateRuleConfigError(key: string, value: string): any {
      const obj = { ...self.config.ruleConfigErrors, [key]: value }
      self.config.ruleConfigErrors = obj
    },
    updateAttributesCount(count: number) {
      self.config.txnAttributesCount = count
    },
  }))
  .views((self) => ({
    getCampaigns(): any {
      const retData: any = []
      self.campaigns?.map((item: any) => {
        const campaignItem = {
          id: item.cId,
          cId: item.cId,
          campaignTitle: item?.title?.slice(0, 42) + (item?.title?.length > 45 ? '...' : ''),
          startDate: item.startDate,
          endDate: item.endDate,
          campaignType: item.discount,
          rewards:
            Number(item?.rewardsDistribution?.rewardsDistributed ?? 0) === 0
              ? '0'
              : `${Number(item?.rewardsDistribution?.rewardsDistributed ?? 0)}/${Number(
                  item?.rewardsDistribution?.distributionCount ?? 1,
                )}`,
          status: item.status,
          disabled: item.status === 'COMPLETED',
        }
        retData.push(campaignItem)
      })
      return retData?.length === 0 ? [] : [...retData]
    },
    getSummary(): any {
      return self.highlights
    },
    getTitle(id: string): any {
      return self?.campaigns?.filter((item: any) => item.cId === id)?.[0]?.title ?? ''
    },
    getStatus(id: string): any {
      return self?.campaigns?.filter((item: any) => item.cId === id)?.[0]?.status ?? ''
    },
    getCampaignDetails(id: string): any {
      const campaignDetails = self?.searchedCampaign
      const getMode = (mode: any) => {
        const keys: any = {
          579: 'Wallet',
          581: 'NetBanking',
          582: 'Card',
        }
        return keys[mode]
      }

      const getPaymodeKey = () => {
        const keys: any = {
          579: 'Wallets',
          581: 'Banks',
          582: 'Card Types',
        }
        return keys[campaignDetails?.paymentModes]
      }

      const getPayModeValues = () => {
        if (campaignDetails?.paymentModes == 582) {
          return campaignDetails?.bankIds
        }
      }

      const getSymbolName = (value: string) => {
        const symbol = value.charAt(0)
        const names: any = {
          '<': 'Less than',
          '>': 'Greater than',
        }
        return names[symbol] ?? 'Range'
      }

      const getAllProducts = () => {
        if (!!campaignDetails.products && !isEmptyObject(campaignDetails.products)) {
          let productsApplicableData: any = []
          let productsNotApplicableData: any = []
          const prodObj: any = campaignDetails.products
          const keys = Object.keys(prodObj)
          const getKey = (item: any) => {
            const keysArr: any = {
              txnAttributeIncluded1: 'txnAttributeKey1',
              txnAttributeExcluded1: 'txnAttributeKey1',
              txnAttributeIncluded2: 'txnAttributeKey2',
              txnAttributeExcluded2: 'txnAttributeKey2',
              txnAttributeIncluded3: 'txnAttributeKey3',
              txnAttributeExcluded3: 'txnAttributeKey3',
              txnAttributeIncluded4: 'txnAttributeKey4',
              txnAttributeExcluded4: 'txnAttributeKey4',
              txnAttributeIncluded5: 'txnAttributeKey5',
              txnAttributeExcluded5: 'txnAttributeKey5',
              txnAttributeIncluded6: 'txnAttributeKey6',
              txnAttributeExcluded6: 'txnAttributeKey6',
            }
            return prodObj[keysArr[item]]
          }
          keys?.map((item: any) => {
            const obj = {
              key: getKey(item),
              value: prodObj[item],
            }
            switch (item) {
              case 'txnAttributeIncluded1':
              case 'txnAttributeIncluded2':
              case 'txnAttributeIncluded3':
              case 'txnAttributeIncluded4':
              case 'txnAttributeIncluded5':
              case 'txnAttributeIncluded6':
                obj?.value?.length > 0 && productsApplicableData.push(obj)
                break
              case 'txnAttributeExcluded1':
              case 'txnAttributeExcluded2':
              case 'txnAttributeExcluded3':
              case 'txnAttributeExcluded4':
              case 'txnAttributeExcluded5':
              case 'txnAttributeExcluded6':
                obj?.value?.length > 0 && productsNotApplicableData.push(obj)
                break

              default:
                break
            }
          })

          if (productsNotApplicableData?.length > 0 || productsApplicableData?.length > 0) {
            return {
              eventType: 'Product purchase',
              applicableProducts: productsApplicableData,
              notApplicableProducts: productsNotApplicableData,
            }
          }
        }
      }

      const getEmiDetails = () => {
        return campaignDetails?.emiTenure?.map((item, index) => {
          if (campaignDetails?.roi?.length > 0) {
            return `${campaignDetails?.emiBankName?.[index]}; ${item}; ${campaignDetails?.roi?.[index]}%\n`
          }
          return `${campaignDetails?.emiBankName?.[index]}; ${item}\n`
        })
      }
      const data = {
        id: id,
        campaignDetails: {
          title: campaignDetails.title,
          startDate: `${campaignDetails?.startDate} ${campaignDetails?.startTime}`,
          endData: `${campaignDetails?.endDate} ${campaignDetails?.endTime}`,
          description: campaignDetails.description,
          tnc: campaignDetails.tnc,
        },
        rules: [
          {
            eventType: 'Successful Transaction',
            txnCriteria: campaignDetails?.txnAmountRange,
            bin: campaignDetails?.bins?.length > 0 ? campaignDetails?.bins?.toString()?.replaceAll(',', ', ') : '',
            paymode:
              campaignDetails?.purchaseTypes.includes('114') && campaignDetails?.roi?.length > 0
                ? 'No Cost EMI'
                : getMode(campaignDetails?.paymentModes),
            [`${getPaymodeKey()}`]: getPayModeValues(),
            emiTenure: removeDuplicateElement(campaignDetails?.emiTenure)?.toString(),
            emiBankName: removeDuplicateElement(campaignDetails?.emiBankName)?.join(','),
            roi:
              campaignDetails?.purchaseTypes.includes('114') && campaignDetails?.roi?.length > 0
                ? campaignDetails?.roi?.join(',')
                : undefined,
          },
          {
            products: getAllProducts(),
          },
        ],
        rewards: {
          offerType: campaignDetails?.discount,
          discountValue: `${campaignDetails?.value} ${
            campaignDetails?.discountType?.toLowerCase() === 'percentage' ? '%' : ''
          }`,
          maxCashback: campaignDetails?.offerMaxCount,
          iscampaignLinked: campaignDetails?.combinedWith?.length > 0 ? 'Yes' : 'No',
          linkedCampaigns: campaignDetails?.combinedWith?.length > 0 ? campaignDetails?.combinedWith : undefined,
        },
      }
      return data
    },

    getCampaignDetailsWithId(id: string): any {
      if (!id) {
        return {}
      }
      const data = [self?.searchedCampaign]
      const getSymbolName = (value: string) => {
        const symbol = value.charAt(0)
        const names: any = {
          '<': 'Less than',
          '>': 'Greater than',
        }
        return names[symbol] ?? 'Range'
      }
      const keys: any = {
        579: 'Wallet',
        581: 'NetBanking',
        582: 'Card',
      }

      const getInstruments = (type: any, item: any) => {
        const instruments: any = {
          579: item.bankIds,
          581: item.bankIds,
          582: item.cardTypes,
        }
        return instruments[type]
      }

      const getFormattedTime = (time: any) => {
        const timeArray = time.split(':')
        let hr = Number(timeArray[0])
        let meridian = 'AM'
        if (hr === 0) {
          hr = 12
        }
        if (hr > 12) {
          meridian = 'PM'
          hr = hr - 12
        }
        const hour = hr < 10 ? '0' + hr : hr
        return hour + ':' + timeArray[1] + ':' + meridian
      }
      const setAttributeCount = (data: any) => {
        let count = 0

        for (let i = 0; i < 6; i++) {
          if (!isEmpty(data[`txnAttributeKey${i}`])) {
            // (self as any).updateProductSelection(data[`txnAttributeKey${i}`])
            count = count + 1
          }
        }
        ;(self as any).updateTxnAttributesCount(count)
      }

      const getPurchaseTypes = (purchaseTypes: any) => {
        const arr = typeof purchaseTypes === 'string' ? purchaseTypes?.split(',') : purchaseTypes
        const purchaseTypeString: any = {
          110: 'Regular Purchase',
          114: 'EMI Purchase',
        }
        return arr?.map((item: any) => purchaseTypeString[item])
      }
      const retData = data.map((item: any) => {
        self.config.txnAttributesCount <= 0 && setAttributeCount(item.products)

        const includedExcludedCheckedObj: any = {}
        for (let i = 1; i <= 6; i++) {
          includedExcludedCheckedObj[`isExcludedIdChecked${i}`] =
            item.products?.[`txnAttributeExcluded${i}`]?.length > 0
          includedExcludedCheckedObj[`isIncludedIdChecked${i}`] =
            item.products?.[`txnAttributeIncluded${i}`]?.length > 0
        }
        return {
          campaignId: id,
          title: item.title,
          description: item?.description ?? '',
          startTime: getFormattedTime(item.startTime),
          endTime: getFormattedTime(item.endTime),
          startDate: item?.startTimestamp,
          endDate: item?.endTimestamp,
          totalBudget: item?.maxBudget,
          totalCount: item?.offerMaxCount,
          tnc: item?.tnc,
          ruleConfig: {
            txnAmountKey: getSymbolName(item?.txnAmountRange),
            txnAmount: getSymbolName(item?.txnAmountRange) !== 'Range' ? `${item?.txnAmountRange}`.slice(1) : undefined,
            minAmount: `${item?.txnAmountRange}`?.split('-')?.[0]?.slice(1)?.trim(),
            maxAmount: `${item?.txnAmountRange}`?.split('-')?.[1]?.slice(2)?.trim(),
            purchaseType: getPurchaseTypes(item?.purchaseTypes),
            emiBankName: item?.emiBankName,
            tenure: item?.purchaseTypes.includes('114') && item?.roi?.length > 0 ? item?.emiTenure : undefined,
            emiTenure: item?.purchaseTypes.includes('114') && item?.roi?.length > 0 ? undefined : item?.emiTenure,
            maxLimit: item?.issueCountPerUser,
            roi: item?.roi,
            paymentMode:
              item?.purchaseTypes.includes('114') && item?.roi?.length > 0 ? 'No Cost EMI' : keys[item.paymentModes],
            bin:
              item.bins?.length > 0 && (keys[item.paymentModes] === 'Card' || item?.purchaseTypes.includes('114'))
                ? item.bins
                : undefined,

            paymentInstrument: getInstruments(item.paymentModes, item),
            ...item.products,
            ...includedExcludedCheckedObj,
          },
          discountValue: item.discountType,
          maxCashback: item?.offerMaxCount,
          offerType: item?.discount,
          discountType: item?.discountType,
          rewardAmount: item.discountType === 'Fixed' ? item?.value : undefined,
          rewardPercentage: item.discountType === 'Percentage' ? item?.value : undefined,
          linkedCampaignId: item?.combinedWith?.length > 0 ? item?.combinedWith?.split(',') : [],
        }
      })
      return retData?.[0] ?? {}
    },
    getAllPaymentModes(): any {
      return self.paymentModes?.filter((item) => item.selected === false)
    },

    getAllProducts(): any {
      return self.config.productSelection?.filter((item) => item.selected === false)
    },
    getRuleConfigErrors(): any {
      return self.config.ruleConfigErrors
    },
    getAttributesCount() {
      return self.config.txnAttributesCount
    },
    getLineIndicatorsData(id: string): any {
      const campaignDetails = self?.campaigns?.filter((item: any) => item.cId === id)?.[0]
      if (campaignDetails) {
        const startDate = campaignDetails?.startTimestamp
        const endDate = campaignDetails?.endTimestamp
        const currentDate = new Date().getTime()
        const totalPeriod = 1 + (endDate - startDate) / (1000 * 60 * 60 * 24)
        const completedPeriod = (currentDate - startDate) / (1000 * 60 * 60 * 24)
        const expiryPeriod = totalPeriod - Math.ceil(completedPeriod)
        const expiryPercentage = Math.ceil(completedPeriod) / totalPeriod
        const usedBudget =
          campaignDetails?.maxBudget > 0 ? (Number(campaignDetails?.maxBudget) / campaignDetails?.maxBudget) * 100 : 0

        const distributionPercentage = campaignDetails?.maxBudget
        const lineIndicatorData = [
          {
            id: 1,
            lineColor: '#FF7E7E',
            title: 'Expiry:',
            percentage: expiryPercentage,
            infoText: expiryPeriod < 0 ? 'Expired' : `${Math.floor(expiryPeriod)} days left`,
            textColour: 'TEXT_ERROR',
          },
          {
            id: 2,
            lineColor: '#F7D51E',
            title: 'Budget:',
            percentage: usedBudget,
            infoText: `${usedBudget.toFixed(2)}% Used`,
            textColour: '#C1A300',
          },
          {
            id: 3,
            lineColor: '#8FE6A3',
            title: 'Distribution:',
            percentage: distributionPercentage / 100,
            infoText: `${distributionPercentage}% Success`,
            textColour: '#12802B',
          },
        ]
        return lineIndicatorData
      } else {
        return []
      }
    },
    getInstruments(key: string, selectedBank?: string): any {
      if (key === 'emiTenure') {
        const data = self.paymentInstruments.filter(
          (filterItem: any) => filterItem?.type?.toLocaleLowerCase() === 'bankemiid',
        )?.[0]
        const tenureData = data?.values?.filter((item: any) => selectedBank === item?.value) ?? []
        const emiTenureData = tenureData?.[0]?.tenures?.map((tenureItem: any, index: number) => {
          return {
            id: index,
            value: tenureItem,
          }
        })
        return {
          key: 'Select EMI Tenure',
          array: emiTenureData ?? [],
        }
      } else {
        const mode = key === 'No Cost EMI' ? 'bankEmiId' : key
        let retItem: any = []
        self?.paymentInstruments?.map((item) => {
          if (item?.type?.toLocaleLowerCase() === mode?.toLocaleLowerCase()) {
            retItem = item?.values ?? []
          }
        })
        return JSON.parse(JSON.stringify(retItem))
      }
    },
    getNoCostEmiBanksList() {
      const own: any = self
      const bankTenureArr = own.getInstruments('bankEmiId')
      return bankTenureArr?.map((item) => {
        return item?.value
      })
    },
    getGraphData(bgColor: any): any {
      const labels: any = []
      const values: any = []
      self.graphData?.map((item) => {
        labels.push(item?.date)
        values.push(item?.value)
      })
      const nonZeroValues = values.filter((item: any) => item !== 0)
      const retData = {
        labels: nonZeroValues?.length > 0 ? labels : [],
        datasets: [
          {
            data: values,
            backgroundColor: bgColor,
            fill: true,
            borderColor: '#1386B0',
            cubicInterpolationMode: 'monotone',
            pointBackgroundColor: '#1386B0',
            pointRadius: 1.5,
            borderWidth: 1,
          },
        ],
      }
      return retData
    },
  }))

const CampaignsData = Campaigns.create({
  campaigns: [],
  paymentModes: [],
  config: {
    productSelection: [
      { id: 1, value: 'Category Id - 1', selected: false },
      { id: 2, value: 'Category Id - 2', selected: false },
      { id: 3, value: 'Category Id - 3', selected: false },
      { id: 4, value: 'Category Id - 4', selected: false },
      { id: 5, value: 'Category Id - 5', selected: false },
      { id: 6, value: 'Product Id', selected: false },
    ],
  },
  graphData: [],
  duration: 'Weekly',
  selectedDateInfo: {
    fromDate: getSelectedDate(0),
    toDate: new Date(Date.now()),
    position: {
      top: 0,
      left: 0,
      visible: false,
    },
    dateLabel: 'Today',
  },
})

export { Campaigns, CampaignsData }
